/**
 * useContactExport.ts
 * 
 * Hook personalizado para exportar contactos en múltiples formatos.
 * Soporta: JSON, CSV, XLSX, PDF, TXT
 * 
 * @version 1.0.0
 * @date 2024-11-06
 */

import { useCallback } from 'react';
import { ContactDB } from '../data/mockContactsDatabase';

// ==================== 📋 TYPES ====================

export type ExportFormat = 'json' | 'csv' | 'xlsx' | 'pdf' | 'txt';

export interface ExportOptions {
  format: ExportFormat;
  filename?: string;
  fields?: (keyof ContactDB)[];
  includeHeaders?: boolean;
}

export interface UseContactExportResult {
  exportContacts: (contacts: ContactDB[], options: ExportOptions) => void;
  isExporting: boolean;
}

// ==================== 🔧 HELPERS ====================

/**
 * Descargar archivo
 */
const downloadFile = (content: string | Blob, filename: string, mimeType: string) => {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Formatear fecha para nombre de archivo (YYYYMMDD-HHMMSS)
 */
const getTimestamp = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  
  return `${year}${month}${day}-${hours}${minutes}${seconds}`;
};

/**
 * Escapar CSV
 */
const escapeCsvValue = (value: any): string => {
  if (value === null || value === undefined) return '';
  
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

// ==================== 📤 EXPORTADORES ====================

/**
 * Exportar a JSON
 */
const exportToJSON = (contacts: ContactDB[], fields?: (keyof ContactDB)[]): string => {
  let data = contacts;
  
  // Filtrar campos si se especifican
  if (fields && fields.length > 0) {
    data = contacts.map(contact => {
      const filtered: any = {};
      fields.forEach(field => {
        filtered[field] = contact[field];
      });
      return filtered;
    });
  }
  
  return JSON.stringify(data, null, 2);
};

/**
 * Exportar a CSV
 */
const exportToCSV = (
  contacts: ContactDB[], 
  fields?: (keyof ContactDB)[],
  includeHeaders: boolean = true
): string => {
  // Campos por defecto
  const defaultFields: (keyof ContactDB)[] = ['name', 'phone', 'email', 'company', 'status'];
  const selectedFields = fields && fields.length > 0 ? fields : defaultFields;
  
  const lines: string[] = [];
  
  // Headers
  if (includeHeaders) {
    lines.push(selectedFields.map(field => escapeCsvValue(field)).join(','));
  }
  
  // Datos
  contacts.forEach(contact => {
    const row = selectedFields.map(field => {
      const value = contact[field];
      
      // Arrays como strings separados por ;
      if (Array.isArray(value)) {
        return escapeCsvValue(value.join(';'));
      }
      
      return escapeCsvValue(value);
    });
    
    lines.push(row.join(','));
  });
  
  return lines.join('\n');
};

/**
 * Exportar a XLSX (usando SheetJS-style manual)
 */
const exportToXLSX = (
  contacts: ContactDB[],
  fields?: (keyof ContactDB)[],
  includeHeaders: boolean = true
): Blob => {
  // Campos por defecto
  const defaultFields: (keyof ContactDB)[] = ['name', 'phone', 'email', 'company', 'status', 'created_at'];
  const selectedFields = fields && fields.length > 0 ? fields : defaultFields;
  
  // Construir tabla HTML
  let html = '<html><head><meta charset="utf-8"></head><body><table>';
  
  // Headers
  if (includeHeaders) {
    html += '<tr>';
    selectedFields.forEach(field => {
      html += `<th style="font-weight: bold; background-color: #f0f0f0; padding: 8px; border: 1px solid #ccc;">${field}</th>`;
    });
    html += '</tr>';
  }
  
  // Datos
  contacts.forEach(contact => {
    html += '<tr>';
    selectedFields.forEach(field => {
      const value = contact[field];
      const displayValue = Array.isArray(value) ? value.join('; ') : value || '';
      html += `<td style="padding: 8px; border: 1px solid #ccc;">${displayValue}</td>`;
    });
    html += '</tr>';
  });
  
  html += '</table></body></html>';
  
  // Crear blob XLSX
  const blob = new Blob([html], { 
    type: 'application/vnd.ms-excel' 
  });
  
  return blob;
};

/**
 * Exportar a PDF (usando window.print con estilos)
 */
const exportToPDF = (
  contacts: ContactDB[],
  fields?: (keyof ContactDB)[]
): void => {
  // Campos por defecto
  const defaultFields: (keyof ContactDB)[] = ['name', 'phone', 'email', 'company', 'status'];
  const selectedFields = fields && fields.length > 0 ? fields : defaultFields;
  
  // HTML para PDF
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Contactos - ${new Date().toLocaleDateString('es-CL')}</title>
  <style>
    @media print {
      @page { 
        size: A4; 
        margin: 20mm; 
      }
      body { 
        margin: 0;
        padding: 0;
      }
    }
    
    body { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; 
      margin: 20px;
      color: #333;
    }
    h1 { 
      color: #27c2b0; 
      margin-bottom: 10px;
      font-size: 24px;
    }
    .meta {
      color: #666; 
      margin-bottom: 20px;
      font-size: 12px;
    }
    table { 
      width: 100%; 
      border-collapse: collapse; 
      font-size: 11px;
      page-break-inside: auto;
    }
    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    th { 
      background-color: #27c2b0; 
      color: white; 
      padding: 10px 8px; 
      text-align: left; 
      border: 1px solid #ddd;
      font-weight: 600;
    }
    td { 
      padding: 8px; 
      border: 1px solid #ddd;
      background-color: white;
    }
    tr:nth-child(even) td { 
      background-color: #f9f9f9; 
    }
    .footer { 
      margin-top: 20px; 
      font-size: 10px; 
      color: #666;
      text-align: center;
      border-top: 1px solid #ddd;
      padding-top: 10px;
    }
    .print-button {
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 10px 20px;
      background: #27c2b0;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      z-index: 1000;
    }
    .print-button:hover {
      background: #1fa794;
    }
    @media print {
      .print-button {
        display: none;
      }
    }
  </style>
</head>
<body>
  <button class="print-button" onclick="window.print()">🖨️ Imprimir / Guardar PDF</button>
  
  <h1>📋 Contactos Exportados</h1>
  <p class="meta">
    <strong>Total de contactos:</strong> ${contacts.length}<br>
    <strong>Fecha de exportación:</strong> ${new Date().toLocaleString('es-CL')}<br>
    <strong>Campos incluidos:</strong> ${selectedFields.join(', ')}
  </p>
  
  <table>
    <thead>
      <tr>
        ${selectedFields.map(field => `<th>${field}</th>`).join('')}
      </tr>
    </thead>
    <tbody>
      ${contacts.map((contact, idx) => `
        <tr>
          ${selectedFields.map(field => {
            const value = contact[field];
            const displayValue = Array.isArray(value) 
              ? value.join(', ') 
              : value || '-';
            return `<td>${displayValue}</td>`;
          }).join('')}
        </tr>
      `).join('')}
    </tbody>
  </table>
  
  <div class="footer">
    <p>
      <strong>Documento generado automáticamente</strong><br>
      Sistema de Gestión de Contactos - WASP Frontend v2.0<br>
      ${new Date().toLocaleString('es-CL')}
    </p>
    <p style="font-size: 9px; color: #999; margin-top: 10px;">
      Para guardar como PDF: Ctrl+P (Windows) o Cmd+P (Mac) → Seleccionar "Guardar como PDF"
    </p>
  </div>
</body>
</html>
  `;
  
  // Abrir en nueva ventana para imprimir
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    
    // Auto-print después de cargar
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.focus();
        // El usuario decide si imprime o guarda como PDF
      }, 250);
    };
  }
};

/**
 * Exportar a TXT
 */
const exportToTXT = (
  contacts: ContactDB[],
  fields?: (keyof ContactDB)[]
): string => {
  // Campos por defecto
  const defaultFields: (keyof ContactDB)[] = ['name', 'phone', 'email', 'company', 'status'];
  const selectedFields = fields && fields.length > 0 ? fields : defaultFields;
  
  let txt = '═══════════════════════════════════════════════════════════\n';
  txt += '                    CONTACTOS EXPORTADOS\n';
  txt += '═══════════════════════════════════════════════════════════\n';
  txt += `Total de contactos: ${contacts.length}\n`;
  txt += `Fecha: ${new Date().toLocaleString('es-CL')}\n`;
  txt += '═══════════════════════════════════════════════════════════\n\n';
  
  contacts.forEach((contact, index) => {
    txt += `CONTACTO #${index + 1}\n`;
    txt += '─────────────────────────────────────────────────────────\n';
    
    selectedFields.forEach(field => {
      const value = contact[field];
      let displayValue: string;
      
      if (Array.isArray(value)) {
        displayValue = value.join(', ');
      } else {
        displayValue = value ? String(value) : '-';
      }
      
      // Formatear campo
      const fieldName = field.replace(/_/g, ' ').toUpperCase();
      txt += `${fieldName}: ${displayValue}\n`;
    });
    
    txt += '\n';
  });
  
  return txt;
};

// ==================== 🎣 HOOK ====================

export const useContactExport = (): UseContactExportResult => {
  
  /**
   * Exportar contactos
   */
  const exportContacts = useCallback((
    contacts: ContactDB[],
    options: ExportOptions
  ) => {
    const { format, filename, fields, includeHeaders = true } = options;
    
    // Generar nombre de archivo
    const timestamp = getTimestamp();
    const defaultFilename = `contacts_${timestamp}`;
    const finalFilename = filename || defaultFilename;
    
    try {
      switch (format) {
        case 'json': {
          const json = exportToJSON(contacts, fields);
          downloadFile(json, `${finalFilename}.json`, 'application/json');
          break;
        }
        
        case 'csv': {
          const csv = exportToCSV(contacts, fields, includeHeaders);
          downloadFile(csv, `${finalFilename}.csv`, 'text/csv');
          break;
        }
        
        case 'xlsx': {
          const xlsx = exportToXLSX(contacts, fields, includeHeaders);
          downloadFile(xlsx, `${finalFilename}.xlsx`, 'application/vnd.ms-excel');
          break;
        }
        
        case 'pdf': {
          exportToPDF(contacts, fields);
          // No se descarga, se abre ventana para imprimir/guardar
          break;
        }
        
        case 'txt': {
          const txt = exportToTXT(contacts, fields);
          downloadFile(txt, `${finalFilename}.txt`, 'text/plain');
          break;
        }
        
        default:
          console.error('Formato de exportación no soportado:', format);
      }
    } catch (error) {
      console.error('Error exportando contactos:', error);
      throw error;
    }
  }, []);
  
  return {
    exportContacts,
    isExporting: false
  };
};

export default useContactExport;